if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.playActiveMediaTimer = null;
        this.syncMainViewerButtons = this.syncMainViewerButtons.bind(this);
        this.onMainViewerButtonClick = this.onMainViewerButtonClick.bind(this);

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.viewer.slider?.addEventListener('scroll', debounce(() => {
          const activeThumbnailButton = this.elements.thumbnails?.querySelector('button[aria-current="true"]');
          const activeThumbnailItem = activeThumbnailButton?.closest('[data-target]');
          this.syncMainViewerButtons(activeThumbnailItem?.dataset?.target);
        }, 120));
        this.elements.viewer.querySelector('.slider-button--prev')?.addEventListener('click', this.onMainViewerButtonClick, {
          capture: true,
        });
        this.elements.viewer.querySelector('.slider-button--next')?.addEventListener('click', this.onMainViewerButtonClick, {
          capture: true,
        });
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        window.requestAnimationFrame(() => this.resetViewerPages());
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.syncMainViewerButtons();
      }

      onSlideChanged(event) {
        this.setActiveMediaState(event.detail.currentElement);
        this.schedulePlayActiveMedia(event.detail.currentElement);
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        window.requestAnimationFrame(() => {
          this.syncMainViewerButtons(event.detail.currentElement.dataset.mediaId);
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth', block: "center" });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.syncMainViewerButtons(mediaId);
      }

      syncMainViewerButtons(activeMediaId) {
        const sliderButtons = this.elements.viewer?.querySelector('.slider-buttons');
        const prevButton = this.elements.viewer?.querySelector('.slider-button--prev');
        const nextButton = this.elements.viewer?.querySelector('.slider-button--next');
        if (!sliderButtons || !prevButton || !nextButton) return;

        const thumbnailItems = Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || [])
          .filter(el => el.offsetParent !== null);
        const totalItems = thumbnailItems.length;
        if (totalItems <= 1) {
          sliderButtons.style.display = 'none';
          return;
        }

        sliderButtons.style.display = '';
        const activeThumbnailButton = this.elements.thumbnails?.querySelector('button[aria-current="true"]');
        const activeThumbnailItem = activeThumbnailButton?.closest('[data-target]');
        const currentMediaId =
          activeMediaId ||
          activeThumbnailItem?.dataset.target ||
          this.elements.viewer.querySelector('.product__media-item.is-active')?.dataset.mediaId ||
          thumbnailItems[0]?.dataset.target;
        const currentIndex = thumbnailItems.findIndex((item) => item.dataset.target === currentMediaId);
        const isFirst = currentIndex <= 0;
        const isLast = currentIndex === -1 || currentIndex >= totalItems - 1;

        prevButton.classList.toggle('is-gallery-edge', isFirst);
        nextButton.classList.toggle('is-gallery-edge', isLast);
        prevButton.toggleAttribute('hidden', isFirst);
        nextButton.toggleAttribute('hidden', isLast);

        if (isFirst) {
          prevButton.setAttribute('disabled', 'disabled');
        } else {
          prevButton.removeAttribute('disabled');
          prevButton.removeAttribute('hidden');
        }

        if (isLast) {
          nextButton.setAttribute('disabled', 'disabled');
        } else {
          nextButton.removeAttribute('disabled');
          nextButton.removeAttribute('hidden');
        }
      }

      onMainViewerButtonClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.resetViewerPages();

        const thumbnailItems = Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || [])
          .filter(el => el.offsetParent !== null);
        if (thumbnailItems.length <= 1) return;

        const activeThumbnailButton = this.elements.thumbnails?.querySelector('button[aria-current="true"]');
        const activeThumbnailItem = activeThumbnailButton?.closest('[data-target]');
        const currentMediaId =
          activeThumbnailItem?.dataset.target ||
          this.elements.viewer.querySelector('.product__media-item.is-active')?.dataset.mediaId ||
          thumbnailItems[0]?.dataset.target;
        const currentIndex = thumbnailItems.findIndex((item) => item.dataset.target === currentMediaId);
        if (currentIndex === -1) return;

        const nextIndex = event.currentTarget.name === 'next' ? currentIndex + 1 : currentIndex - 1;
        const nextItem = thumbnailItems[nextIndex];
        if (!nextItem) {
          this.syncMainViewerButtons(currentMediaId);
          return;
        }

        this.scrollToMedia(nextItem.dataset.target);
      }

      resetViewerPages() {
        if (this.elements.viewer?.resetPages) this.elements.viewer.resetPages();
      }

      scrollToMedia(mediaId) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        const slider = this.elements.viewer?.slider || activeMedia?.parentElement;
        if (!activeMedia || !slider) return;

        this.setActiveMediaState(activeMedia);
        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) this.setActiveThumbnail(activeThumbnail);
        this.syncMainViewerButtons(mediaId);
        this.schedulePlayActiveMedia(activeMedia);

        slider.scrollTo({
          left: activeMedia.offsetLeft,
          behavior: 'smooth',
        });
      }

      setActiveMediaState(activeMedia) {
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        console.log('slide not visible');
        if (this.elements.thumbnails.simplebar) {
          this.elements.thumbnails.querySelector('.simplebar-content-wrapper').scrollTo({
            left: thumbnail.offsetLeft,
            behavior: 'smooth',
          });
        } else {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        deferredMedia.loadContent(false);

        window.requestAnimationFrame(() => {
          const video = deferredMedia.querySelector('video');
          if (video) video.play().catch(() => {});
        });
      }

      schedulePlayActiveMedia(activeItem) {
        if (!activeItem) return;

        window.clearTimeout(this.playActiveMediaTimer);
        this.playActiveMediaTimer = window.setTimeout(() => {
          if (!activeItem.classList.contains('is-active')) return;
          this.playActiveMedia(activeItem);
        }, 550);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
